import { useCallback, useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import startsWith from 'lodash/startsWith';
import { useController, Control, FieldValues, FieldPath } from 'react-hook-form';

import { useFileUploader, useClipboard } from '@ts-core/core/hooks/utils';
import { arrayMove } from '@ts-core/core/components/Sort';
import { ExerciseMediaType } from '@ts-core/modules/exercises/types';

import { useProfessorAuth } from 'modules/auth/hooks';

import { ExerciseMediaItem } from '../types';
import { useExerciseMediaActions } from '../hooks';

const getEndpointUrl = (mediaType: ExerciseMediaType): string => {
  switch (mediaType) {
    case 'audio':
      return 'aws/signed-url/audios';
    case 'video':
      return 'aws/signed-url/videos';
    case 'image':
      return 'aws/signed-url/questions';
  }
};
const getMediaType = (contentType: string): ExerciseMediaType | undefined => {
  if (startsWith(contentType, 'audio')) {
    return 'audio';
  } else if (startsWith(contentType, 'video')) {
    return 'video';
  } else if (startsWith(contentType, 'image')) {
    return 'image';
  }
  return undefined;
};

interface ExerciseMediaFieldInput<TField extends FieldValues> {
  control: Control<TField, any>;
  name: FieldPath<TField>;
}

interface ExerciseMediaFieldOutput {
  acceptedFiles: string[];

  onFiledSelected: (file: File) => void;

  onDeleteMediaAtIndex: (index: number) => void;
  onSortMedia: (from: number, to: number) => void;

  mediaItems: ExerciseMediaItem[];
  uploading?: boolean;
  uploadError?: string;
  uploadProgress: number;
  selectedFileName?: string;
}

export default function useExerciseMediaField<TField extends FieldValues>(input: ExerciseMediaFieldInput<TField>): ExerciseMediaFieldOutput {
  const { control, name } = input;
  const { courseId, subjectId } = useParams<{ courseId: string; subjectId: string }>();
  const { field } = useController({ control, name });
  const { onChange, value } = field;
  const [selectedFile, setSelectedFile] = useState<File>();
  const { onCreateExerciseMediaItem, createExerciseMediaItemLoading } = useExerciseMediaActions({ courseId, subjectId });
  const { user } = useProfessorAuth();
  const acceptedFiles = useMemo(() => {
    return ['image/*', 'audio/*', 'video/*'];
  }, []);

  const { uploading, uploadProgress, onUploadFile, error } = useFileUploader();

  const onAppendNewItem = useCallback(
    (item: ExerciseMediaItem) => {
      const newValue = [...(value || []), item];
      onChange(newValue);
      setSelectedFile(undefined);
    },
    [onChange, value]
  );

  const handleFileUploaded = useCallback(
    (mediaType: ExerciseMediaType, fileName: string, url: string, path: string, awsFileName?: string) => {
      if (mediaType === 'image') {
        onAppendNewItem({ type: mediaType, url, path, id: path });
      } else {
        if (awsFileName) {
          onCreateExerciseMediaItem({ type: mediaType, awsFileName, name: fileName, belongsToExercise: true }, onAppendNewItem);
        }
      }
    },
    [onCreateExerciseMediaItem, onAppendNewItem]
  );

  const onFiledSelected = useCallback(
    (file: File) => {
      const contentType = file.type;
      setSelectedFile(file);
      const mediaType = getMediaType(contentType);
      if (mediaType) {
        onUploadFile(
          file,
          getEndpointUrl(mediaType),
          {},
          {
            headers: { 'x-amz-tagging': `academy=${user?.academy?._id}` },
            onSuccess: (data) => {
              handleFileUploaded(mediaType, file.name, data.url, data.path, data.awsFileName);
            },
          }
        );
      }
    },
    [onUploadFile, handleFileUploaded, user]
  );

  useClipboard({ acceptedFiles, onFilePasted: onFiledSelected });

  const onDeleteMediaAtIndex = useCallback(
    (index: number) => {
      if (!Array.isArray(value)) return;
      const newValue = [...value.slice(0, index), ...value.slice(index + 1)];
      onChange(newValue);
    },
    [value, onChange]
  );

  const onSortMedia = useCallback(
    (from: number, to: number) => {
      onChange(arrayMove(value, from, to));
    },
    [value, onChange]
  );

  return {
    mediaItems: value || [],
    acceptedFiles,

    onFiledSelected,
    onDeleteMediaAtIndex,
    onSortMedia,

    uploading: uploading || createExerciseMediaItemLoading,
    uploadError: error,
    uploadProgress,
    selectedFileName: selectedFile?.name,
  };
}
